import {useMutation} from '@tanstack/react-query';
import {useState} from 'react';
import {api} from '../api';
import {useNotifications} from '../lib';

interface UploadPrepareItem {
  id: string;
  url: string;
}

interface PrepareUploadResponse {
  items: UploadPrepareItem[];
}

interface UsePublicImageUploadOptions {
  resourceName?: string;
  notificationKey?: string;
}

interface UsePublicImageUploadResult {
  uploadId: string | null;
  fileName: string | null;
  fileSize: number | null;
  previewUrl: string | null;
  isUploading: boolean;
  uploadImage: (file: File) => void;
  resetUpload: () => void;
}

const uploadFileDirectly = async (file: File, uploadUrl: string): Promise<void> => {
  const buffer = await file.arrayBuffer();
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: buffer,
  });

  if (!response.ok) {
    let errorBody = 'Unknown error during direct upload.';
    try {
      errorBody = await response.text();
    } catch (e) {
      // ignore
    }
    throw new Error(`Direct upload failed with status ${response.status}: ${errorBody}`);
  }
};

export const usePublicImageUpload = ({
  resourceName = 'Изображение',
  notificationKey = 'public-image-upload-error',
}: UsePublicImageUploadOptions = {}): UsePublicImageUploadResult => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const {createNotification} = useNotifications();

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<{id: string; file: File}> => {
      const {data: response} = await api.post<PrepareUploadResponse>('/api/files/public/prepare-upload', {
        contentTypes: [file.type],
      });

      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      await uploadFileDirectly(file, response.items[0].url);

      return {id: response.items[0].id, file};
    },
    onSuccess: ({id, file}) => {
      setUploadId(id);
      setFileName(file.name);
      setFileSize(file.size);

      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    },
    onError: (err: Error) => {
      createNotification({
        name: notificationKey,
        title: `Ошибка загрузки изображения${resourceName ? ` (${resourceName.toLowerCase()})` : ''}`,
        content: err.message,
        theme: 'danger',
      });
      setUploadId(null);
      setFileName(null);
      setFileSize(null);
      setPreviewUrl(null);
    },
  });

  const resetUpload = () => {
    setUploadId(null);
    setFileName(null);
    setFileSize(null);
    setPreviewUrl(null);
    uploadMutation.reset();
  };

  return {
    uploadId,
    fileName,
    fileSize,
    previewUrl,
    isUploading: uploadMutation.isPending,
    uploadImage: uploadMutation.mutate,
    resetUpload,
  };
};
